import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    title = "title",
    questions = "questions",
    questionHeading = "heading",
    questionDescription = "description",
    spacingTop = "spacingTop",
    spacingBottom = "spacingBottom",

    displayTitle = "Title",
    displayQuestions = "Questions",
    displayQuestionHeading = "Question",
    displayQuestionDescription = "Answer",
    displaySpacingTop = "Spacing top",
    displaySpacingBottom = "Spacing bottom",
}

export interface QuestionModel {
    fields: {
        [fields.questionHeading]: string;
        [fields.questionDescription]: string;
    };
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.questions]: QuestionModel[];
        [fields.spacingTop]: number;
        [fields.spacingBottom]: number;
    };
}

export const mapStateToProps = (_: ApplicationState, __: OwnProps) => {
    return {};
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface FaqWidgetStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    accordion?: InjectableCss;
    accordionContent?: InjectableCss;
}

export const faqWidgetStyles: FaqWidgetStyles = {};

export const styles = faqWidgetStyles;

export const FaqWidget = ({ fields: { title, questions, spacingTop, spacingBottom } }: Props) => {
    const wrapperStyle = styles.wrapper ?? {};
    wrapperStyle.css = css`
        ${wrapperStyle?.css}
        margin-top: ${spacingTop ?? 0}px;
        margin-bottom: ${spacingBottom ?? 0}px;
    `;

    return (
        <StyledWrapper {...wrapperStyle}>
            <Typography {...styles.title}>{title}</Typography>
            <Accordion headingLevel={3} {...styles.accordion}>
                {questions.map(({ fields: { heading, description } }) => (
                    <AccordionSection key={`faq_widget__key-${heading.replace(/\s/g, "-").toLowerCase()}`} title={heading}>
                        <StyledWrapper {...styles.accordionContent}>{parse(description, parserOptions)}</StyledWrapper>
                    </AccordionSection>
                ))}
            </Accordion>
        </StyledWrapper>
    );
};

const definitionTab: TabDefinition = {
    displayName: "Definition",
    sortOrder: 1,
};

const stylesTab: TabDefinition = {
    displayName: "Styles",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(FaqWidget) as any,
    definition: {
        displayName: "FAQ",
        group: "Verndale Box" as any,
        icon: "question",
        fieldDefinitions: [
            // Definition tab items:
            {
                tab: definitionTab,
                name: fields.title,
                displayName: fields.displayTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                tab: definitionTab,
                name: fields.questions,
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: fields.questionHeading,
                        displayName: fields.displayQuestionHeading,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: fields.questionDescription,
                        displayName: fields.displayQuestionDescription,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                ],
                getDisplay: (item: HasFields) => item.fields[fields.questionHeading],
            },

            // Styles tab items:
            {
                tab: stylesTab,
                name: fields.spacingTop,
                displayName: fields.displaySpacingTop,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
            },
            {
                tab: stylesTab,
                name: fields.spacingBottom,
                displayName: fields.displaySpacingBottom,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
